import { useRemoteSensors } from "@/hooks/useRemoteSensors";
import { t, Trans } from "@lingui/macro";
import { Box, Container, FormControl, Paper, TextField, Typography, useTheme } from "@mui/material";
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  ChangeEventHandler,
  FocusEventHandler,
} from "react";
import { useUnitFormatter } from "@/hooks/useUnitFormatter";
import {
  BlueSwitch,
  MenuList,
  MenuListItemVariant,
  RedesignrFormControlLabel,
  RedesignrFormControlLabelContent,
  RedesignrRadio,
  RedesignrRadioGroup,
} from "@WahooFitness/redesignr";
import { WSMWheelCircumferenceCfg } from "@WahooFitness/wsm-native/dist/esm/types/wheel_circumference_cfg";
import { GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";

interface WheelCircumferenceConfigProps {
  appToken: string;
  remoteSensorId: number;
}

const WheelCircumferenceSelectionComponent: React.FC<WheelCircumferenceConfigProps> = ({
  appToken,
  remoteSensorId,
}: WheelCircumferenceConfigProps) => {
  const { remoteSensorStatus, getWheelCircumferenceOptions, saveRemoteSensorConfig } =
    useRemoteSensors(appToken);
  const { alwaysMetricUnitFormatter } = useUnitFormatter();
  const { palette } = useTheme();

  const remoteSensor = remoteSensorStatus?.sensors?.find(
    (sensor) => sensor?.cfg?.sensor_id === remoteSensorId
  );

  const wheelCircumferenceInM = remoteSensor?.cfg?.wheel_circ_mm
    ? (remoteSensor?.cfg?.wheel_circ_mm / 1000.0).toFixed(3)
    : 0;
  const units = alwaysMetricUnitFormatter?.formatElevation(wheelCircumferenceInM).formattedUnit;

  const [wheelOptions, setWheelOptions] = useState<WSMWheelCircumferenceCfg[]>([]);

  const [wheelCircumference, setWheelCircumference] = useState<string>("");

  const [wheelCircumferenceValid, setWheelCircumferenceValid] = useState<boolean>(true);

  useEffect(() => {
    setWheelCircumference(wheelCircumferenceInM.toString());
  }, [wheelCircumferenceInM]);

  const handleBlur: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = useCallback(
    (event) => {
      if (!wheelCircumferenceValid) {
        setWheelCircumference(wheelCircumferenceInM.toString());
        return;
      }

      const newValue = +event.target.value;
      if (newValue !== +event.target.value) {
        setWheelCircumference(newValue.toString());
      }
    },
    [wheelCircumferenceValid, wheelCircumferenceInM]
  );

  const handleWheelCircumferenceChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> =
    useCallback(
      (event) => {
        setWheelCircumferenceValid(
          !Number.isNaN(+event.target.value) &&
            +event.target.value > 0 &&
            +event.target.value <= 65.535
        );
        setWheelCircumference(event.target.value);
        saveRemoteSensorConfig(remoteSensorId, { wheel_circ_mm: +event.target.value * 1000 });
      },
      [remoteSensorId, saveRemoteSensorConfig]
    );

  useEffect(() => {
    const fetchWheelOptions = async () => {
      try {
        const options = await getWheelCircumferenceOptions();
        setWheelOptions(options || []);
      } catch (error) {
        console.error("Failed to fetch wheel circumference options:", error);
      }
    };

    fetchWheelOptions();
  }, [getWheelCircumferenceOptions]);

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      handleWheelCircumferenceChange(event);
    },
    [handleWheelCircumferenceChange]
  );

  const wheelCircumferenceItems = useMemo(() => {
    if (!remoteSensor) {
      return [];
    }

    return (
      <Box display="flex" flexDirection="column" width="100%" gap={2}>
        <FormControl sx={{ width: "100%" }}>
          <RedesignrRadioGroup
            name="single-select-radio-button-group"
            onChange={onChange}
            value={+wheelCircumference}
          >
            {wheelOptions.map((option) => {
              const circumferenceInMeters = option.circumference;
              return (
                <RedesignrFormControlLabel
                  key={option.description}
                  value={option.circumference}
                  control={<RedesignrRadio />}
                  label={
                    <RedesignrFormControlLabelContent
                      align="left"
                      label={`${circumferenceInMeters}${units}`}
                      secondary={`${option.description}\u2003\u2003${option.description_iso}`}
                    />
                  }
                />
              );
            })}
          </RedesignrRadioGroup>
        </FormControl>
      </Box>
    );
  }, [onChange, remoteSensor, units, wheelCircumference, wheelOptions]);

  if (!remoteSensorStatus) {
    return null;
  }

  const inputMode = "decimal";

  return (
    <>
      <GlobalHeader title={t`Wheel circumference`} />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          width="100%"
          height="100%"
        >
          <Box
            width="100%"
            display="flex"
            flexDirection="column"
            overflow="scroll"
            py={2}
            px={2}
            gap={4}
          >
            <MenuList
              listItems={[
                {
                  id: "auto_wheel_circumference",
                  content: t`Automatic`,
                  hidden: remoteSensor?.cfg?.is_wheel_circ_auto_calib_enabled === undefined,
                  variant: MenuListItemVariant.Action,
                  actionComponent: (
                    <Box display="flex" flexDirection="row" gap={1}>
                      <BlueSwitch checked={remoteSensor?.cfg?.is_wheel_circ_auto_calib_enabled} />
                    </Box>
                  ),
                  action: () => {
                    saveRemoteSensorConfig(remoteSensorId, {
                      is_wheel_circ_auto_calib_enabled:
                        !remoteSensor?.cfg?.is_wheel_circ_auto_calib_enabled,
                    });
                  },
                },
              ]}
              disableGutters
            ></MenuList>
            {remoteSensor?.cfg?.is_wheel_circ_auto_calib_enabled !== undefined && (
              <Box mx={2} mt={-4}>
                <Typography variant="ui-base-medium" color={palette.text.secondary}>
                  <Trans>
                    When enabled, ELEMNT will use GPS from your outdoor rides to pick the most
                    accurate wheel circumference for your bike.
                  </Trans>
                </Typography>
              </Box>
            )}
            {!remoteSensor?.cfg?.is_wheel_circ_auto_calib_enabled && (
              <Paper elevation={4}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  px={1}
                  py={1}
                >
                  <Typography variant="prose-base-medium" noWrap mx={1}>
                    <Trans>Custom size</Trans>
                  </Typography>
                  <Box textAlign="right" width="40%">
                    {
                      <TextField
                        variant="outlined"
                        value={wheelCircumference} // Remove the units from the value
                        size="small"
                        type="text" // Change to text type since we're showing units
                        InputProps={{
                          endAdornment: <Box component="span">{units}</Box>, // Add units as endAdornment
                        }}
                        inputProps={{
                          style: { textAlign: "right" },
                          inputMode: inputMode, // Keep this for numeric keyboard on mobile
                        }}
                        onBlur={handleBlur}
                        onChange={handleWheelCircumferenceChange}
                        error={!wheelCircumferenceValid}
                        helperText={
                          wheelCircumferenceValid
                            ? undefined
                            : t`Enter a value between 0 and 65.535`
                        }
                        fullWidth
                      />
                    }
                  </Box>
                </Box>
              </Paper>
            )}
            {!remoteSensor?.cfg?.is_wheel_circ_auto_calib_enabled && wheelCircumferenceItems}
          </Box>
        </Box>
      </Container>
    </>
  );
};

export { WheelCircumferenceSelectionComponent };
